import { ApolloClient, HttpLink, InMemoryCache, concat, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { notifyGraphqlError } from './bugsnag/events';
import { getJwt } from './storage';
import { get15DigitId } from '../utils/helpers/salesforce';

const errorLink = onError(notifyGraphqlError);

const httpLink = new HttpLink({
  // Default uri to /graphql if not testing
  uri: ({ operationName }) =>
    `${
      window.location.origin.substr(0, 17) === 'https://localhost'
        ? `https://${import.meta.env.VITE_CALIX_ENV || 'dev'}-store.calix.com`
        : ''
    }/graphql?op=${operationName}`,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'x-api-key': import.meta.env.VITE_GRAPHQL_API_KEY,
      authorization: `Bearer ${getJwt()}`,
    },
  };
});

const removeTypenameLink = removeTypenameFromVariables();

export const client = new ApolloClient({
  link: from([removeTypenameLink, concat(errorLink, authLink.concat(httpLink))]),
  connectToDevTools: true,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getAccount: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'Account',
                accountId: get15DigitId(args?.accountId || ''),
              });
            },
          },
        },
      },
      Quote: {
        keyFields: ['transactionId'],
      },
      Account: {
        keyFields: (object) => {
          if (object && typeof object.accountId === 'string') {
            return `Account:${get15DigitId(object.accountId)}`;
          }
          return false; // Disable normalization if accountId is invalid
        },
        fields: {
          listQuotes: {
            read(existing) {
              return existing;
            },
            merge(existing, incoming, options) {
              if (!existing) {
                return incoming;
              }

              // When removing a quote, the list will already be filtered
              // so we can simply update the cache with provided info
              if (options?.variables?.operation === 'remove') {
                return incoming;
              }

              // When adding a new quote, we will add it to the beginning of the list
              if (options?.variables?.operation === 'add') {
                return {
                  ...incoming,
                  quotes: [...(incoming.quotes || []), ...(existing.quotes || [])],
                };
              }

              // When no operation is specified, we will merge the results (normal list quotes call)
              return {
                ...incoming,
                quotes: [...(existing.quotes || []), ...(incoming.quotes || [])],
              };
            },
          },
          getQuote: {
            read(_, { args, toReference }) {
              return toReference({
                __typename: 'Quote',
                transactionId: args?.transactionId,
              });
            },
          },
        },
      },
      SearchPartsResponse: {
        keyFields: ['partNumber'],
      },
    },
  }),
});
